import { useCallback, useMemo } from 'react';

import { useLoggedInUserRoles } from '~/components/Auth';
import { useApplicationConfig } from '~/data/applicationConfig';
import { useEntityAttributes } from '~/data/attributes';
import { CostCenter, useAllCostCenters } from '~/data/costCenters';
import {
  EmpBasicData,
  EmpEmeContact,
  EmpHomeContact,
  EmpPosition,
  EmployeeCostCenter,
  EmploymentData,
  useEmployee,
} from '~/data/employee';
import { EmployeesIndexed, useAllEmployees } from '~/data/employees';
import {
  EmploymentDetailType,
  useEmploymentDetails,
} from '~/data/employment/useEmploymentDetails';
import { LegalEntity, useAllLegalEntities } from '~/data/legalEntity';
import { useAllOrgUnits, useOrgUnitHRSupervisor } from '~/data/orgunits';
import { useOrgUnitsEmpHasResponsibilityOver } from '~/data/orgunits/useOrgUnitsEmpHasResponsibilityOver';
import { ProposalType } from '~/data/proposals';
import { useGetProposals } from '~/data/proposals/useGetProposals';
import { Roles } from '~/data/user';
import { useEmploymentData } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/Employment/useEmploymentData';
import { useContracts } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useContracts';
import { useSchedulePendingEmployment } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useSchedulePendingEmployment';
import { useSelectedEmployeeId } from '~/hooks/employee/useSelectedEmployeeId';
import { UseDialogStateOutput, useDialogState } from '~/hooks/useDialogState';
import {
  ScheduledEmploymentOption,
  useScheduleEmployments,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/useScheduleEmployments';
import { EmployeeStatusEnum } from '~/services/Http/client.people.generated';
import { isRoleHR, isRoleManager } from '~/services/Permissions';

import { hasCustomFieldsMarkerForCostCenter } from './components/EmploymentDataAllCostCenters';
import {
  getAllOrganizationUnitNames,
  getManagerName,
  getOrganizationUnitName,
  getUnitNamesEmployeeIsResponsibleFor,
} from './data';

export type Props = {
  defaultScheduleButtonName: string;
};

type PositionCostCenterInfo = {
  primary?: EmployeeCostCenter;
  hasCustomDataFields: boolean;
  additional: EmployeeCostCenter[];
};

export type UseEmploymentDataFormOutput = {
  employeeId: number;
  empHomeContact: EmpHomeContact;
  empEmeContact: EmpEmeContact;
  allEmployees: EmployeesIndexed;
  addOverlappingEmployment: {
    isEnabled: boolean;
  };
  scheduleEmploymentChange: {
    dialog: UseDialogStateOutput;
    isEnabled: boolean;
    isLoading: boolean;
  };
  employmentHistory: {
    isEnabled: boolean;
  };
  rehireEmployee: {
    isEnabled: boolean;
  };
  employmentPositionColumn: {
    empPosition: EmpPosition;
    empPositionPending?: EmpPosition;
    costCenters: CostCenter[];
    legalEntities: LegalEntity[] | undefined;
    pendingUnitName?: string;
    getAllOrgUnitNames: (position?: EmpPosition) => string;
    positionCostCenters: {
      current: PositionCostCenterInfo;
      pending: PositionCostCenterInfo;
    };
    relationsInfo: {
      empDeputyManager?: number;
      managerResponsibilityUnits: number[];
      alternateManagerResponsibilityUnits: number[];
      hrResponsibilityUnits: number[];
      getUnitNamesEmployeeIsResponsibleForCallback: (
        responsibilityUnits: number[],
      ) => string;

      getManagerName: (employeeId?: number) => string;
    };
    newEmployment?: ScheduledEmploymentOption;
    empBasicData: EmpBasicData;
  };
  employmentDetailsColumn: {
    employmentDetails?: EmploymentDetailType;
    pendingEmploymentDetails?: EmploymentDetailType;
    employmentData: EmploymentData;
  };
  employmentDetail?: EmploymentDetailType;
  isLoading: boolean;
  failedToLoad: boolean;
};

export const useEmploymentDataForm = (): UseEmploymentDataFormOutput => {
  // Load general data
  const { applicationConfig } = useApplicationConfig();

  const myRoles = useLoggedInUserRoles();
  const { orgUnits } = useAllOrgUnits();

  const overlappingEmploymentsEnabled =
    applicationConfig.fComConfig.miscellaneousConfig
      .fComEnableOverlappingEmployments;
  const managerAddOverlappingEmploymentEnabled =
    applicationConfig.fComConfig.mgrConfig.fComEnableMgrAddOverlapEmployment;

  const { allEmployees, isLoading: isLoadingAllEmployees } = useAllEmployees();
  const getMgrName = useCallback(
    (managerId?: number) => {
      return !!managerId ? getManagerName(allEmployees, managerId) : '';
    },
    [allEmployees],
  );

  const {
    costCenters,
    isLoading: costCentersLoading,
    hasData: costCenterHasData,
  } = useAllCostCenters();
  const {
    legalEntities,
    isLoading: legalEntitiesLoading,
    hasData: legalEntityHasData,
  } = useAllLegalEntities();
  const { isLoading: attributesIsLoading } = useEntityAttributes();

  const {
    selectedEmployeeId,
    selectedEmployeeDetails: { employeeData },
  } = useSelectedEmployeeId();
  const employmentId = employeeData?.employmentId;

  const {
    employee: {
      empBasicData,
      empPosition,
      empPositionPending,
      fEmpBusinessUnits: empBusinessUnits,
      empDeputyManagers,
      empHomeContact,
      empEmeContact,
    },
    isLoading: employeeLoading,
  } = useEmployee();

  const scheduledDialogState = useDialogState();

  const { data: emplDetails, isLoading: employmentDetailsLoading } =
    useEmploymentDetails(selectedEmployeeId, employmentId);

  const { isLoading: pendingDataLoading } =
    useSchedulePendingEmployment(selectedEmployeeId);
  const { isLoading: contractsLoading } = useContracts(
    empBasicData.fEmpProfileId,
  );
  const { isLoading: proposalDetailsLoading } = useGetProposals({
    targetId: selectedEmployeeId,
    proposalType: ProposalType.EMPLOYMENT_DETAILS_PROPOSAL,
  });

  const getAllOrgUnitNames = useCallback(
    (position?: EmpPosition): string => {
      return getAllOrganizationUnitNames(empBusinessUnits, orgUnits, position);
    },
    [empBusinessUnits, orgUnits],
  );

  const getOrgUnitName = useCallback(
    (unitId?: number): string | undefined => {
      return getOrganizationUnitName(orgUnits, unitId);
    },
    [orgUnits],
  );

  const { currentCostCenterData, pendingCostCenterData } = useMemo(() => {
    const resolveCostCenterData = (
      position?: EmpPosition,
    ): PositionCostCenterInfo => {
      const primary = position?.fEmpCostCenters.find(
        (costCenter) => costCenter.fEmpCostCenterIsDefault,
      );

      const hasCustomDataFields = hasCustomFieldsMarkerForCostCenter(
        primary?.fEmpCostCenterCustomData,
      );

      const additional = !!position
        ? position.fEmpCostCenters.filter((cc) => {
            return !cc.fEmpCostCenterIsDefault;
          })
        : [];

      return {
        primary,
        hasCustomDataFields,
        additional,
      };
    };

    return {
      currentCostCenterData: resolveCostCenterData(empPosition),
      pendingCostCenterData: resolveCostCenterData(empPositionPending),
    };
  }, [empPosition, empPositionPending]);

  const pendingUnitName = getOrgUnitName(
    empPositionPending?.fInternalPositionUnit,
  );

  const { isLoading: isHRSuperLoading } = useOrgUnitHRSupervisor({
    orgUnitId: empPosition?.fInternalPositionUnit,
  });

  // Employment Details
  const { employmentDetails, pendingEmploymentDetails } = useMemo(() => {
    return {
      employmentDetails: emplDetails
        ? emplDetails.find(
            (empDetails) => empDetails.fEmplUid === EmployeeStatusEnum.ACTIVE,
          )
        : undefined,
      pendingEmploymentDetails: emplDetails
        ? emplDetails.find(
            (empDetails) => empDetails.fEmplUid === EmployeeStatusEnum.PENDING,
          )
        : undefined,
    };
  }, [emplDetails]);

  const {
    selectedEmployeeId: employeeId,
    isSelectedEmployeeLoggedInUser,
    selectedEmployeeDetails: { hasRoleAccessOverEmployee },
  } = useSelectedEmployeeId();
  const {
    data: [employmentData],
    isLoading: employmentDataLoading,
  } = useEmploymentData({ employeeId });

  // deputy managers
  const empDeputyManager = empDeputyManagers && empDeputyManagers[0];

  const {
    data: {
      managerResponsibilityUnits,
      alternateManagerResponsibilityUnits,
      hrResponsibilityUnits,
    },
  } = useOrgUnitsEmpHasResponsibilityOver({
    employeeId,
  });

  const { orgUnits: allUnits, isLoading: isLoadingAllUnits } = useAllOrgUnits();

  const { hasUpcomingEmployments, scheduledEmploymentOptions } =
    useScheduleEmployments();
  const newEmployment = hasUpcomingEmployments
    ? scheduledEmploymentOptions.find((emp) => emp.positionIsPending)
    : undefined;

  const getUnitNamesEmployeeIsResponsibleForCallback = useCallback(
    (responsibilityUnits: number[]) =>
      getUnitNamesEmployeeIsResponsibleFor(responsibilityUnits, allUnits),
    [allUnits],
  );

  const isLoading =
    (legalEntitiesLoading && !legalEntityHasData) ||
    (costCentersLoading && !costCenterHasData) ||
    employeeLoading ||
    isLoadingAllEmployees ||
    employmentDetailsLoading ||
    pendingDataLoading ||
    contractsLoading ||
    proposalDetailsLoading ||
    isHRSuperLoading ||
    attributesIsLoading ||
    employmentDataLoading ||
    isLoadingAllUnits;

  const {
    isAddOverlappingEnabled,
    isScheduleChangeEnabled,
    isHistoryEnabled,
    isRehireEnabled,
  } = useMemo(() => {
    const isEndedEmployee = empBasicData?.fEmpStatus === 'ENDED';
    const isPendingEmployee = empBasicData?.fEmpStatus === 'PENDING';

    const canEditEmployment = isRoleHR(myRoles) || isRoleManager(myRoles);

    const isAddOverlappingEnabled =
      overlappingEmploymentsEnabled &&
      (isRoleHR(myRoles) ||
        (isRoleManager(myRoles) && managerAddOverlappingEmploymentEnabled));

    const canViewHistory =
      isSelectedEmployeeLoggedInUser ||
      isRoleHR(myRoles) ||
      hasRoleAccessOverEmployee([
        Roles.MANAGER,
        Roles.ALT_MANAGER,
        Roles.DEPUTY_MANAGER,
      ]); // TODO: Should include other roles?

    return {
      isAddOverlappingEnabled,
      isScheduleChangeEnabled:
        canEditEmployment && !isEndedEmployee && !isPendingEmployee,
      isRehireEnabled: canEditEmployment && !isLoading && isEndedEmployee,
      isHistoryEnabled: canViewHistory && !isLoading,
    };
  }, [
    isLoading,
    empBasicData,
    overlappingEmploymentsEnabled,
    managerAddOverlappingEmploymentEnabled,
    isSelectedEmployeeLoggedInUser,
    myRoles,
    hasRoleAccessOverEmployee,
  ]);

  return {
    isLoading,
    failedToLoad: false,
    employeeId: selectedEmployeeId,
    empHomeContact,
    empEmeContact,
    allEmployees,
    addOverlappingEmployment: {
      isEnabled: isAddOverlappingEnabled,
    },
    scheduleEmploymentChange: {
      dialog: scheduledDialogState,
      isEnabled: isScheduleChangeEnabled,
      isLoading: isLoading || !emplDetails,
    },
    employmentHistory: {
      isEnabled: isHistoryEnabled,
    },
    rehireEmployee: {
      isEnabled: isRehireEnabled,
    },
    employmentPositionColumn: {
      empPosition,
      empPositionPending,
      costCenters,
      legalEntities,
      empBasicData,
      newEmployment,
      pendingUnitName,
      getAllOrgUnitNames,
      positionCostCenters: {
        current: currentCostCenterData,
        pending: pendingCostCenterData,
      },
      relationsInfo: {
        empDeputyManager,
        hrResponsibilityUnits,
        managerResponsibilityUnits,
        alternateManagerResponsibilityUnits,
        getUnitNamesEmployeeIsResponsibleForCallback,
        getManagerName: getMgrName,
      },
    },
    employmentDetailsColumn: {
      employmentDetails,
      pendingEmploymentDetails,
      employmentData,
    },
  };
};
